import { useTranslator } from "@eo-locale/react";
import { KeyboardEvent, useEffect } from "react";

import {
  Kbd,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useMediaQuery
} from "@/storybook-components";
import AccessibilityButton from "./accessibility-button";

const AccessibilityKeysModal = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { translate } = useTranslator();
  const [isDesktop] = useMediaQuery("(min-width: 768px)");

  useEffect(() => {
    const handleKeyDown: any = (event: KeyboardEvent) => {
      if (event.key === "k" && (event.metaKey || event.altKey)) {
        event.preventDefault();
        onOpen();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const onCancelOrClose = () => {
    onClose();
  };

  const handleFilterClick = () => {
    onOpen();
  };
  // eslint-disable-next-line no-undef
  const isMacUser = navigator.userAgent.includes("Macintosh");
  const keyboardKey = isMacUser ? (
    <>
      <Kbd>⌘</Kbd> + <Kbd>k</Kbd>
    </>
  ) : (
    <>
      <Kbd>⎇</Kbd> + <Kbd>k</Kbd>
    </>
  );
  const optionBtn = isMacUser ? "option" : "alt";
  const keyboardText = isMacUser ? "cmd + k" : `${optionBtn} + k`;

  return (
    <>
      <AccessibilityButton handleFilterClick={handleFilterClick} />
      <Modal
        onClose={onCancelOrClose}
        isOpen={isOpen}
        size={isDesktop ? "md" : "xs"}
        isCentered
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {translate("places.keyboard.keyboardShortcut")}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <TableContainer>
              <Table variant="simple">
                <Thead>
                  <Tr>
                    <Th>{translate("places.keyboard.shortcut")}</Th>
                    <Th>{translate("places.keyboard.description")}</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  <Tr
                    tabIndex={0}
                    aria-label={`${optionBtn} + shift + s , ${translate(
                      "places.keyboard.search"
                    )}`}
                  >
                    <Td>
                      <Kbd>⎇</Kbd> + <Kbd>shift</Kbd> + <Kbd>s</Kbd>
                    </Td>
                    <Td>{translate("places.keyboard.search")} </Td>
                  </Tr>
                  <Tr
                    tabIndex={0}
                    aria-label={`${keyboardText}, ${translate(
                      "places.keyboard.keyboard"
                    )}`}
                  >
                    <Td> {keyboardKey}</Td>
                    <Td>{translate("places.keyboard.keyboard")} </Td>
                  </Tr>
                  <Tr
                    tabIndex={0}
                    aria-label={`${optionBtn} + shift + n , ${translate(
                      "places.pin.new"
                    )}`}
                  >
                    <Td>
                      <Kbd>⎇</Kbd> + <Kbd>shift</Kbd> + <Kbd>n</Kbd>
                    </Td>
                    <Td>{translate("places.pin.new")} </Td>
                  </Tr>
                  <Tr
                    tabIndex={0}
                    aria-label={`shift + up arrow , ${translate(
                      "places.keyboard.pinUp"
                    )}`}
                  >
                    <Td>
                      <Kbd>shift</Kbd> + <Kbd> &uarr; </Kbd>
                    </Td>
                    <Td>{translate("places.keyboard.pinUp")} </Td>
                  </Tr>
                  <Tr
                    tabIndex={0}
                    aria-label={`shift + left arrow , ${translate(
                      "places.keyboard.pinLeft"
                    )}`}
                  >
                    <Td>
                      <Kbd>shift</Kbd> + <Kbd> &larr; </Kbd>
                    </Td>
                    <Td>{translate("places.keyboard.pinLeft")} </Td>
                  </Tr>
                  <Tr
                    tabIndex={0}
                    aria-label={`shift + down arrow, ${translate(
                      "places.keyboard.pinDown"
                    )}`}
                  >
                    <Td>
                      <Kbd>shift</Kbd> + <Kbd> &darr; </Kbd>
                    </Td>
                    <Td>{translate("places.keyboard.pinDown")} </Td>
                  </Tr>
                  <Tr
                    tabIndex={0}
                    aria-label={`shift + right arrow , ${translate(
                      "places.keyboard.pinRight"
                    )}`}
                  >
                    <Td>
                      <Kbd>shift</Kbd> + <Kbd> &rarr; </Kbd>
                    </Td>
                    <Td>{translate("places.keyboard.pinRight")} </Td>
                  </Tr>
                </Tbody>
              </Table>
            </TableContainer>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

export default AccessibilityKeysModal;
